import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { ROLES, SALES_RECORDERS } from "../../constants/roles";
import { useCurrentUser } from "../../utilities/currentUser";
import { clearIdempotencyKey, getIdempotencyKey } from "../../utilities/idempotencyKey";
import { userHasAnyRole } from "../../utils/leadershipRoles";

const lookupErrorText = (error) => {
    const status = error?.response?.status;
    if (status === 429) return "Too many customer lookups. Wait a minute and try again.";
    if (status === 404) return "No customer found with that exact email or phone. Check it, or add them as a new customer.";
    const payload = error?.response?.data;
    const firstFieldError = payload?.errors ? Object.values(payload.errors).flat()[0] : null;
    return firstFieldError || payload?.message || "Customer lookup failed. Please try again.";
};

export default function MakeSalePage() {
    const navigate = useNavigate();
    const { user } = useCurrentUser();
    // A Marketing Officer (without a wider sales role) sells as themselves and never browses the user
    // list: they look a customer up by exact email / phone, or add a new one.
    const officerMode =
        userHasAnyRole(user, [ROLES.MARKETING_OFFICER]) && !userHasAnyRole(user, SALES_RECORDERS);
    const [lookupMethod, setLookupMethod] = useState("email");
    const [lookupValue, setLookupValue] = useState("");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState("");
    const [foundCustomer, setFoundCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [customerQuery, setCustomerQuery] = useState("");
    const [formErrors, setFormErrors] = useState([]);
    const submitLock = useRef(false);
    const requestKey = useRef(null);
    
    // Form State
    const [customerType, setCustomerType] = useState("existing");
    const [customers, setCustomers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [units, setUnits] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        customer_id: "",
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_voter_id: "",
        property_id: "",
        unit_id: "",
        total_amount: "",
        paid_amount: "",
        payment_method: "cash"
    });

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        if (officerMode || customerType !== "existing") {
            return undefined;
        }

        const handle = setTimeout(() => {
            fetchCustomers(customerQuery);
        }, 300);

        return () => clearTimeout(handle);
    }, [customerQuery, customerType, officerMode]);

    const clearFoundCustomer = () => {
        setFoundCustomer(null);
        setLookupError("");
        setFormData(prev => ({ ...prev, customer_id: "" }));
    };

    const resetLookup = () => {
        clearFoundCustomer();
        setLookupValue("");
    };

    const handleCustomerTypeChange = (value) => {
        setCustomerType(value);
        setFormErrors([]);
        if (officerMode) resetLookup();
    };

    const handleLookup = async () => {
        const value = lookupValue.trim();
        clearFoundCustomer();

        if (!value) {
            setLookupError(lookupMethod === "email" ? "Enter the customer's email." : "Enter the customer's phone number.");
            return;
        }
        if (lookupMethod === "phone" && !/^\d{11}$/.test(value)) {
            setLookupError("The phone number must be exactly 11 digits.");
            return;
        }

        setLookupLoading(true);
        try {
            const res = await api.get("/api/sales/customer-lookup", { params: { [lookupMethod]: value } });
            const customer = res.data?.data;
            setFoundCustomer(customer);
            setFormData(prev => ({ ...prev, customer_id: String(customer.id) }));
        } catch (error) {
            const message = lookupErrorText(error);
            setLookupError(message);
            toast.error(message);
        } finally {
            setLookupLoading(false);
        }
    };

    const fetchCustomers = async (search = "") => {
        try {
            const res = await api.get("/api/users", {
                params: { per_page: 20, search },
            });
            setCustomers(res.data?.data || []);
        } catch (error) {
            console.error("Failed to fetch customers", error);
        }
    };

    const fetchProperties = async () => {
        try {
            const res = await api.get("/api/get-properties");
            if (res.data && res.data.data) {
                setProperties(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch properties", error);
        }
    };

    const fetchUnits = async (propertyId) => {
        setUnitsLoading(true);
        try {
            const availableUnits = [];
            let page = 1;
            let lastPage = 1;

            do {
                const res = await api.get("/api/get-units", {
                    params: { property_id: propertyId, per_page: 50, page },
                });
                const batch = res.data?.unit || [];
                availableUnits.push(
                    ...batch.filter(
                        (unit) =>
                            unit.status_id === 1 ||
                            unit?.status?.status_type === "available"
                    )
                );
                lastPage = res.data?.last_page || 1;
                page += 1;
            } while (page <= lastPage);

            setUnits(availableUnits);
        } catch (error) {
            console.error("Failed to fetch units", error);
            setUnits([]);
        } finally {
            setUnitsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "property_id") {
            setFormData(prev => ({ ...prev, unit_id: "", total_amount: "" }));
            fetchUnits(value);
        }

        if (name === "unit_id") {
            const selectedUnit = units.find(u => u.id.toString() === value);
            if (selectedUnit) {
                setFormData(prev => ({ ...prev, total_amount: selectedUnit.price || "" }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitLock.current) return;
        if (customerType === "existing" && !formData.customer_id) {
            setFormErrors([officerMode
                ? "Look the customer up by their exact email or phone first, or add a new customer."
                : "Choose a customer."]);
            return;
        }
        submitLock.current = true;
        setLoading(true);
        setFormErrors([]);

        const payload = { ...formData };
        if (customerType === "existing") {
            delete payload.customer_name;
            delete payload.customer_email;
            delete payload.customer_phone;
            delete payload.customer_voter_id;
        } else {
            delete payload.customer_id;
        }

        try {
            const key = getIdempotencyKey(requestKey, JSON.stringify(payload));
            const res = await api.post("/api/sales", payload, { headers: { "Idempotency-Key": key } });
            if (res.data && res.data.status) {
                // Unity commission is sent in the background; never claim it was distributed here.
                alert(res.data.data?.unity_commission?.message || "Sale saved. Commission distribution pending.");
                clearIdempotencyKey(requestKey);
                // Redirect to invoice page
                navigate(`/dashboard/invoice/${res.data.data.invoice.id}`);
            }
        } catch (error) {
            console.error("Sale submission failed", error);
            const payload = error.response?.data;
            const fieldErrors = payload?.errors
                ? Object.values(payload.errors).flat()
                : [payload?.message || "Failed to create sale. Please try again."];
            setFormErrors(fieldErrors);
            toast.error(fieldErrors[0]);
        } finally {
            submitLock.current = false;
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4 mb-5">
            <h2 className="mb-4 text-primary">Make a Sale</h2>
            <div className="card shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        {/* Section 1: Customer Info */}
                        <h5 className="border-bottom pb-2 mb-3">1. Customer Information</h5>
                        <div className="mb-3">
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="customerType" id="existingCust" value="existing" checked={customerType === "existing"} onChange={(e) => handleCustomerTypeChange(e.target.value)} />
                                <label className="form-check-label" htmlFor="existingCust">Existing Customer</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="customerType" id="newCust" value="new" checked={customerType === "new"} onChange={(e) => handleCustomerTypeChange(e.target.value)} />
                                <label className="form-check-label" htmlFor="newCust">New Customer</label>
                            </div>
                        </div>

                        {customerType === "existing" && officerMode ? (
                            <div className="mb-4">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label" htmlFor="lookupMethod">Find by</label>
                                        <select id="lookupMethod" className="form-select" value={lookupMethod} disabled={lookupLoading}
                                            onChange={(e) => { setLookupMethod(e.target.value); resetLookup(); }}>
                                            <option value="email">Exact email</option>
                                            <option value="phone">Exact phone (11 digits)</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" htmlFor="lookupValue">
                                            {lookupMethod === "email" ? "Customer email" : "Customer phone"}
                                        </label>
                                        <input
                                            id="lookupValue"
                                            type={lookupMethod === "email" ? "email" : "text"}
                                            className="form-control"
                                            value={lookupValue}
                                            disabled={lookupLoading}
                                            onChange={(e) => { setLookupValue(e.target.value); clearFoundCustomer(); }}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleLookup(); } }}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <button type="button" className="btn btn-outline-primary w-100" onClick={handleLookup} disabled={lookupLoading}>
                                            {lookupLoading ? "Looking up..." : "Look up customer"}
                                        </button>
                                    </div>
                                </div>
                                {lookupError && <div className="text-danger small mt-2" role="alert">{lookupError}</div>}
                                {foundCustomer && (
                                    <div className="alert alert-success mt-3 mb-0 d-flex justify-content-between align-items-center" role="status">
                                        <div>
                                            <strong>{foundCustomer.name}</strong>
                                            <div className="small">{foundCustomer.email} &middot; {foundCustomer.phone}</div>
                                        </div>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetLookup}>Change</button>
                                    </div>
                                )}
                                <div className="form-text">Customers are found only by their exact email or phone. Their contact details are partly hidden.</div>
                            </div>
                        ) : customerType === "existing" ? (
                            <div className="mb-4 row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Search customer</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Name, email, or phone"
                                        value={customerQuery}
                                        onChange={(e) => setCustomerQuery(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Select Customer</label>
                                    <select className="form-select" name="customer_id" value={formData.customer_id} onChange={handleInputChange} required>
                                        <option value="">-- Choose Customer --</option>
                                        {customers.map(c => (
                                            <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="row mb-4">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Full Name</label>
                                    <input type="text" className="form-control" name="customer_name" value={formData.customer_name} onChange={handleInputChange} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Email</label>
                                    <input type="email" className="form-control" name="customer_email" value={formData.customer_email} onChange={handleInputChange} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Phone Number (11 digits)</label>
                                    <input type="text" className="form-control" name="customer_phone" value={formData.customer_phone} onChange={handleInputChange} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Voter ID / NID</label>
                                    <input type="text" className="form-control" name="customer_voter_id" value={formData.customer_voter_id} onChange={handleInputChange} required />
                                </div>
                            </div>
                        )}

                        {officerMode && (
                            <p className="mb-4 text-muted">Sold by: <strong>you</strong>{user?.name ? ` (${user.name})` : ""}</p>
                        )}

                        {/* Section 2: Property Info */}
                        <h5 className="border-bottom pb-2 mb-3">2. Property Selection</h5>
                        <div className="row mb-4">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Property</label>
                                <select className="form-select" name="property_id" value={formData.property_id} onChange={handleInputChange} required>
                                    <option value="">-- Select Property --</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Unit</label>
                                <select className="form-select" name="unit_id" value={formData.unit_id} onChange={handleInputChange} required disabled={!formData.property_id || unitsLoading || units.length === 0}>
                                    <option value="">{unitsLoading ? "Loading units..." : units.length === 0 && formData.property_id ? "No available units" : "-- Select Unit --"}</option>
                                    {units.map(u => (
                                        <option key={u.id} value={u.id}>{u.unit_number} (Price: {u.price})</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Section 3: Payment Info */}
                        <h5 className="border-bottom pb-2 mb-3">3. Payment Details</h5>
                        <div className="row mb-4">
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Agreed Total Amount (BDT)</label>
                                <input type="number" className="form-control" name="total_amount" value={formData.total_amount} onChange={handleInputChange} required min="1" />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Initial Paid Amount (BDT)</label>
                                <input type="number" className="form-control" name="paid_amount" value={formData.paid_amount} onChange={handleInputChange} required min="0" max={formData.total_amount || undefined} />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Payment Method</label>
                                <select className="form-select" name="payment_method" value={formData.payment_method} onChange={handleInputChange} required>
                                    <option value="cash">Cash</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="cheque">Cheque</option>
                                </select>
                            </div>
                        </div>

                        {formErrors.length > 0 && (
                            <div className="alert alert-danger">
                                {formErrors.map((message) => (
                                    <div key={message}>{message}</div>
                                ))}
                            </div>
                        )}

                        <div className="d-flex justify-content-end">
                            <button type="submit" className="btn btn-primary px-4 py-2" disabled={loading || !formData.unit_id}>
                                {loading ? "Processing..." : "Confirm Sale & Generate Invoice"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
